import * as React from 'react';
import Link from 'next/link';
import { DiscoverLink } from '@/components/atoms/SurfaceLink';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/components/atoms/primitives/utils';
import { categoryDisplayLabel } from '@/lib/city-home/categoryLabelCase';

export interface EventBreadcrumbsProps {
  /** City code rendered as the first segment (e.g. "CLT"). Links to "/". */
  cityCode: string;
  /** Full city name (e.g. "Charlotte"). Used as the accessible label for the city link. */
  cityLabel: string;
  /**
   * Category segment. When a category resolves to a non-empty slug (`{ slug:
   * 'music', label: 'LIVE MUSIC' }`) the crumb links to `/category/<slug>`
   * (slug trimmed + URL-encoded). When `null`, or when the slug is empty /
   * whitespace-only, the crumb renders as non-interactive text (WEB-422) —
   * never a dead `/category/` href, a fabricated `/category/discover` link
   * that 404s, or a `/discover` masquerade that misrepresents the trail. In
   * the blank-slug case the real `label` is shown as plain text; with no
   * category at all it falls back to "EVENTS".
   */
  category: { slug: string; label: string } | null;
  /**
   * Venue segment. When `slug` is a non-empty string the crumb links to
   * `/venue/<slug>`. When `slug` is omitted/empty the crumb renders as
   * non-interactive text (WEB-328 / WEB-809) — never a placeholder
   * `/venue/venue` or a dead `#` link.
   */
  venue: { slug?: string | null; name: string };
  /**
   * WEB-830 — source-published genre crumb, rendered between the category and
   * the venue (`… › MUSIC › ROCK › THE FILLMORE › …`). Mirrors
   * `Event.sourceGenre` (PIP-234).
   *
   * Absent, `null`, or blank omits the `<li>` entirely, so the trail is
   * byte-identical to its pre-WEB-830 five-segment shape. That is the common
   * case by a wide margin — only the Ticketmaster lane publishes a genre, so
   * Eventbrite and UNC-Charlotte events have none, by design rather than by
   * gap.
   *
   * HONEST-DATA CONTRACT: whatever is passed is rendered verbatim. This
   * molecule never infers a genre from the title, the venue, or the artist,
   * and never substitutes a placeholder ("Undefined", "Other", "—") for a
   * missing one. Absence renders as absence.
   *
   * Always **non-interactive text**, never a link: there is no `/genre/<x>`
   * hub page, and WEB-422 / WEB-328 established that a crumb without a real
   * destination renders as plain text rather than as a href that 404s.
   *
   * Casing is the source's own ("Rock", "Hip-Hop"). WEB-1189 C15: the trail
   * is sentence case, so the DOM and the paint agree.
   */
  genre?: string | null;
  /** Event title shown as the active (unlinked) crumb. */
  eventTitle: string;
  /**
   * Label for the category crumb when no category resolves — i.e. `category`
   * is `null`, the common path for an event whose category the taxonomy does
   * not recognise. WEB-704: lets the caller supply the word from the **event
   * lexicon** (`EventLexiconEntry.breadcrumbCategoryLabel`, which degrades to
   * "Events" for an unknown slug) instead of this molecule hardcoding it, so
   * the trail speaks the same vocabulary as the rest of the page.
   *
   * Defaults to {@link NO_CATEGORY_LABEL} so the pre-WEB-704 callers
   * (`EventDetailTemplate`) are unaffected. Only consulted on the unresolved
   * path — when `category` carries a label, that label always wins.
   */
  categoryFallbackLabel?: string;
  /**
   * WEB-605 — set when the nav is a flex child of a `.m-crumbrow` wrapper that
   * owns the `border-b` rule and the vertical padding (so the rule spans the
   * full row, under the trailing Share button). The nav then drops its own
   * `w-full border-b py-3`; `.m-crumbrow > nav` supplies `flex: 1 1 auto;
   * min-width: 0`. Defaults to `false`, so a standalone nav is unchanged.
   */
  inRow?: boolean;
  /** Optional additional class names. */
  className?: string;
}

const CRUMB_LINK_CLASS =
  'inline-flex min-h-[44px] desktop:min-h-0 items-center text-loci-text no-underline transition-colors hover:text-accent-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-clt-skyline-glow motion-reduce:transition-none';

// WEB-328 / WEB-422: styling for an unlinked crumb (venue with no resolvable
// slug, or category that didn't resolve). Matches the muted breadcrumb text
// colour but carries no hover/focus affordance, so it never reads as an
// interactive dead link.
const CRUMB_TEXT_CLASS = 'inline-flex min-h-[44px] items-center text-loci-text-muted';

// WEB-830: styling for the genre crumb, which is unlinked by construction (no
// `/genre/<x>` hub page exists). Mirrors CRUMB_TEXT_CLASS but keeps the links'
// `desktop:min-h-0` reset, because the 44px floor is a TOUCH-TARGET rule and
// this crumb is never a touch target. Without the reset an event whose source
// published a genre would render a taller breadcrumb bar on desktop than one
// whose source did not — the trail's height would encode the presence of a
// genre, which is exactly the kind of accidental signal this crumb must not
// send. (The two `CRUMB_TEXT_CLASS` fallback paths are unaffected: they only
// replace a crumb that would otherwise be a link, and they predate this.)
const CRUMB_GENRE_CLASS =
  'inline-flex min-h-[44px] desktop:min-h-0 items-center text-loci-text-muted';

// WEB-422: label shown for the category crumb when the event has no resolvable
// category. Rendered as plain text (not a link) so it never targets the
// non-existent `/category/discover` route.
const NO_CATEGORY_LABEL = 'Events';

function CrumbSep(): React.ReactElement {
  return <ChevronRight className="inline size-[1em] shrink-0" aria-hidden="true" />;
}

/**
 * EventBreadcrumbs — breadcrumb strip rendered above the event hero.
 *
 * Mirrors the `ev-crumbs` block in
 * `docs/design-system/1.0.0/templates/city/event.html`. Renders five
 * segments separated by a decorative ChevronRight:
 *
 *     CLT › DISCOVER › LIVE MUSIC › THE FILLMORE › SNAIL MAIL
 *
 * WEB-830 adds an optional sixth level between the category and the venue,
 * present only when the source published a genre:
 *
 *     CLT › DISCOVER › LIVE MUSIC › ROCK › THE FILLMORE › SNAIL MAIL
 *
 * Pure presentational server component — no client hooks. Theme-aware via
 * the `--loci-color-*` and `--clt-skyline-glow` CSS variables emitted by
 * `ThemeProvider`, so it renders correctly under both `data-theme="dark"`
 * and `data-theme="light"`.
 */
export function EventBreadcrumbs({
  cityCode,
  cityLabel,
  category,
  genre,
  venue,
  eventTitle,
  categoryFallbackLabel = NO_CATEGORY_LABEL,
  inRow = false,
  className,
}: EventBreadcrumbsProps): React.ReactElement {
  // WEB-830: trimmed once so the "is there a genre" guard and the text that
  // gets rendered can never answer differently.
  const trimmedGenre = typeof genre === 'string' ? genre.trim() : '';

  return (
    <nav
      aria-label="Breadcrumb"
      className={cn('ev-crumbs', !inRow && 'w-full border-b border-loci-border py-3', className)}
      data-testid="event-breadcrumbs"
    >
      <ol className="ev-crumbs__inner flex flex-wrap items-center gap-2 text-micro tracking-[0.08em] text-loci-text-muted font-mono">
        <li className="flex items-center gap-2">
          <Link
            href="/"
            aria-label={cityLabel}
            className={CRUMB_LINK_CLASS}
            data-testid="event-breadcrumbs-city"
          >
            {cityCode}
          </Link>
          <CrumbSep />
        </li>

        <li className="flex items-center gap-2">
          {/* WEB-1231 / R25: signed in → `app.<root>/<city>/discover`. */}
          <DiscoverLink className={CRUMB_LINK_CLASS} data-testid="event-breadcrumbs-discover">
            Discover
          </DiscoverLink>
          <CrumbSep />
        </li>

        <li className="flex items-center gap-2">
          {category != null && category.slug.trim().length > 0 ? (
            <Link
              href={`/category/${encodeURIComponent(category.slug.trim())}`}
              className={CRUMB_LINK_CLASS}
              data-testid="event-breadcrumbs-category"
            >
              {categoryDisplayLabel(category.label)}
            </Link>
          ) : (
            // WEB-422: no resolvable category (null OR an empty/whitespace-only
            // slug) → render plain text rather than linking to a dead
            // `/category/` href, `/category/discover` (a 404), or a `/discover`
            // catch-all that misrepresents the breadcrumb trail. When a label is
            // present (blank-slug case) show the real label; otherwise fall back
            // to `categoryFallbackLabel` (WEB-704: the lexicon's word for this
            // category, defaulting to the generic "EVENTS").
            <span
              className={CRUMB_TEXT_CLASS}
              data-testid="event-breadcrumbs-category"
              data-unlinked="true"
            >
              {category?.label != null
                ? categoryDisplayLabel(category.label)
                : categoryDisplayLabel(categoryFallbackLabel)}
            </span>
          )}
          <CrumbSep />
        </li>

        {/* WEB-830: only when the source actually published a genre. A blank
            or whitespace-only value is absence, not a label — rendering it
            would produce an empty crumb between two `›` separators. */}
        {trimmedGenre.length > 0 ? (
          <li className="flex items-center gap-2">
            <span
              className={CRUMB_GENRE_CLASS}
              data-testid="event-breadcrumbs-genre"
              data-unlinked="true"
            >
              {trimmedGenre}
            </span>
            <CrumbSep />
          </li>
        ) : null}

        <li className="flex items-center gap-2">
          {venue.slug != null && venue.slug.length > 0 ? (
            <Link
              href={`/venue/${encodeURIComponent(venue.slug)}`}
              className={CRUMB_LINK_CLASS}
              data-testid="event-breadcrumbs-venue"
            >
              {venue.name}
            </Link>
          ) : (
            // WEB-328: no resolvable venue slug → render the venue name as
            // plain text instead of a placeholder/dead link. Keeps the
            // breadcrumb trail intact without a 404 dead-end.
            <span
              className={CRUMB_TEXT_CLASS}
              data-testid="event-breadcrumbs-venue"
              data-unlinked="true"
            >
              {venue.name}
            </span>
          )}
          <CrumbSep />
        </li>

        <li>
          <strong
            className="font-medium text-clt-skyline-glow-fg"
            aria-current="page"
            data-testid="event-breadcrumbs-event"
          >
            {eventTitle}
          </strong>
        </li>
      </ol>
    </nav>
  );
}
